import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

const Home = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO 
        title="TerraNova Limited - Geotechnical & Geoenvironmental Engineering"
        description="Channel Islands based consultancy providing geotechnical and geoenvironmental solutions across Jersey & Guernsey. Contaminated land assessment, remediation, foundation engineering and more."
        keywords="geotechnical engineering Jersey, geoenvironmental Guernsey, contaminated land assessment, remediation, foundation engineering, Channel Islands consulting"
        url="/"
      />

      {/* Quick Contact Bar */}
      <div className="bg-TerraNova-rust text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <span className="text-sm">Follow us:</span>
              <a 
                href="https://linkedin.com/company/terranova-ltd" 
                target="_blank"
                style={{zIndex: 1}}
                rel="noopener noreferrer"
                className="hover:text-TerraNova-cream transition duration-300"
                aria-label="Visit our LinkedIn page"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.668v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
            <Link 
              to="/contact"
              className="bg-white text-TerraNova-rust px-4 py-1 rounded text-sm font-semibold hover:bg-TerraNova-cream transition duration-300"
            >
              Quick Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section with Background Video */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '60vh' }}>
        {/* Background Video */}
        {!isMobile && (
          <div className="absolute inset-0 w-full h-full">
            <link rel="preload" href="/images/hero-background.webp" as="image" fetchPriority="high"/>
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              loop
              onMouseEnter={(e) => e.target.setAttribute('preload', 'metadata')}
              muted
              playsInline
              preload="none"
              poster="/images/hero-background.webp" // Fallback poster image
              aria-label="Background video showing geotechnical engineering work"
            >
              <source src="/videos/hero-background.mp4" type="video/mp4" />
              <source src="/videos/hero-background.webm" type="video/webm" />
              {/* Fallback for browsers that don't support video */}
            </video>
            {/* Video overlay for better text readability */}
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
        )}
        
        {/* Mobile/Tablet Background Image Fallback */}
        {isMobile && (
          <>
          <link rel="preload" href="/images/hero-background.webp" as="image" fetchPriority="high"/>
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/images/hero-background.webp)' }}
            aria-label="Background image showing geotechnical engineering work"
          >
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
          </>
        )}

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-lg" style={{'marginTop': 20}}>
            Grounded in Expertise
            <span className="text-TerraNova-rust block">Built on Trust</span>
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto drop-shadow-md">
            TerraNova provides trusted geoenvironmental & geotechnical expertise across the Channel Islands. We focus on our clients needs, delivering high-quality, cost effective advice tailored to local requirements & resources
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" style={{'marginBottom': 20}}>
            <Link 
              to="/contact"
              className="bg-TerraNova-rust text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-TerraNova-clay transition duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Contact Us
            </Link>
            <button 
              onClick={scrollToServices}
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-TerraNova-charcoal transition duration-300 backdrop-blur-sm"
            >
              Our Services
            </button>
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-TerraNova-charcoal mb-4">Our Vision</h2>
            <p className="text-lg text-TerraNova-slate max-w-2xl mx-auto">
              At TerraNova, we believe effective consultancy is built on trust. As a Channel Island based geoenvironmental and geotechnical consultancy, we provide clear, straightforward advice that puts clients first. We avoid unnecessary complexity, focus on your project goals, and deliver solutions that are practical, cost-effective, and tailored to suit an island environment. <br /><br /> Our role is simple: To be a trusted partner who helps you build on solid ground
            </p>
          </div>
        </div>
      </section>

      {/* Comprehensive Services Section */}
      <section id="services-section" className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-TerraNova-cream to-TerraNova-sand">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-4xl md:text-5xl font-bold text-TerraNova-charcoal mb-6">Our Services</h2>
            <p className="text-xl text-TerraNova-slate max-w-4xl mx-auto">
              By combining world-class technical expertise with local knowledge, TerraNova provides clear and cost-effective geoenvironmental and geotechnical services that protect people, safeguard the environment, and support sustainable development across Jersey and Guernsey
            </p>
          </div>

          {/* Services Grid - 50/50 Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Service 1: Contaminated Land Risk Assessment */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Contaminated Land Risk Assessment</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  We assess and manage risks from contaminated land to ensure human health and environmental safety. Our team conducts desk studies, ground investigations, and laboratory testing to identify potential pollutants and evaluate their impact.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h4 className="font-semibold text-TerraNova-charcoal mb-3">Core Services:</h4>
                    <ul className="space-y-2 text-sm text-TerraNova-slate">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Phase 1 Desk Study</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Ground Investigation Design & Supervision</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Phase 2 GQRA & DQRA</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Human health & controlled waters risk assessments</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-TerraNova-charcoal mb-3">Risk Assessments Include:</h4>
                    <ul className="space-y-2 text-sm text-TerraNova-slate">
                      <li className="flex items-start">
                        <span className="text-yellow-500 mr-2">▶</span>
                        <span>Human health risk assessments</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-yellow-500 mr-2">▶</span>
                        <span>Controlled waters risk assessments</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-yellow-500 mr-2">▶</span>
                        <span>Ground gas / vapour risk assessments</span>
                      </li>
                    </ul>
                    <p className="text-xs text-TerraNova-slate mt-3 italic">
                      Potential pollutants include but are not limited to: PFAS, hydrocarbons, heavy metals, pesticides, nitrates, inorganics etc.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 2: Geotechnical Assessment & Design */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Geotechnical Assessment & Design</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  We provide geotechnical assessments to understand sub-surface conditions and inform optimal design. Our investigations include soil and rock testing, groundwater monitoring, and slope stability analysis.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h4 className="font-semibold text-TerraNova-charcoal mb-3">Services Include:</h4>
                    <ul className="space-y-2 text-sm text-TerraNova-slate">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Geotechnical Desk Studies</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Ground investigation design & supervision</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Geotechnical Interpretation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Rockface / Slope Inspections</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Geotechnical Design (shallow foundations, piling, retaining walls, slope stabilisation, rockface stabilisation)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Earthworks Strategy</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 3: Remediation */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Remediation</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  Where contaminated soils and/or waters are encountered - whether during land contamination risk assessment, pollution event, or routine excavation. TerraNova can respond quickly on-site with expert advice and high quality control measures.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h4 className="font-semibold text-TerraNova-charcoal mb-3">Services Include:</h4>
                    <ul className="space-y-2 text-sm text-TerraNova-slate">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Remediation Options Appraisal (ROA)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Remediation Strategy</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Oil Spill Response / Pollution Control</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>On-site Remediation Works (Small-Scale)</span>
                      </li>
                                            <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Supervision of large-scale remediation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Verification / Validation</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 4: Ground Investigation Design & Supervision */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Ground Investigation Design & Supervision</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  In ground investigation, data quality is only as good as the process behind it. TerraNova provides active supervision of drilling, trial pitting, and sampling works to ensure samples are recovered and logged to industry standards, operations remain safe, and regulatory requirements are met.
                </p>
                <p className="text-sm text-TerraNova-slate italic">
                  Proper supervision improves design outcomes, supports real time problem solving on-site, and safeguards project value for the Client, by preventing costly surprises later.
                </p>
              </div>
            </div>

            {/* Service 5: Waste & Resource Management */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Waste & Resource Management</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  We offer waste and resource management solutions that ensure sustainability and regulatory compliance. Services include waste classification, recycling strategies, and preparation of environmental permits.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <h4 className="font-semibold text-TerraNova-charcoal mb-3">Services Include:</h4>
                    <ul className="space-y-2 text-sm text-TerraNova-slate">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Waste Classification / Assessment</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Material Re-use Assessments</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Material Management Plans</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Technical Advice</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 6: Soil & Water Sampling */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" style={{'marginTop': 8}} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2s-4.5 5.5-4.5 9.5a4.5 4.5 0 0 0 9 0C16.5 7.5 12 2 12 2z"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Soil & Water Sampling</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  We provide bespoke soil and water sampling services to assess environmental quality and detect contamination. Using standardised techniques, we collect representative samples for laboratory analysis of pollutants (including PFAS).
                </p>
                <p className="text-sm text-TerraNova-slate italic">
                  The results support land use decisions, remediation planning, and regulatory compliance.
                </p>
              </div>
            </div>

          </div>

          {/* Additional Services - Full Width */}
          <div className="mt-8">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-TerraNova-cream hover:border-TerraNova-rust transition duration-300 hover:shadow-xl">
              <div className="bg-TerraNova-rust text-white px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold">Additional Services</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-TerraNova-slate mb-4">
                  Beyond our core specialisations, TerraNova offers additional environmental and regulatory services to support comprehensive project delivery.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <ul className="space-y-2 text-sm text-TerraNova-slate">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Preliminary Flood Risk Assessments (FRA)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Construction Environmental Management Plans (CEMP)</span>
                      </li>
                                           <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Environmental permitting / due diligence</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Liaison with The Local Authority/Regulator</span>
                      </li>
                                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-TerraNova-rust rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span>Peer Review Papers</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12 bg-white p-8 rounded-xl shadow-lg border-2 border-TerraNova-cream">
            <h3 className="text-2xl font-bold text-TerraNova-charcoal mb-4">Ready to Get Started?</h3>
            <p className="text-lg text-TerraNova-slate mb-6 max-w-2xl mx-auto">
              Contact us today to discuss your geotechnical or geoenvironmental engineering needs. Our expert team is ready to provide tailored solutions for your Channel Islands project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-TerraNova-rust text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-TerraNova-clay transition duration-300 shadow-lg transform hover:-translate-y-1"
              >
                Request Consultation
              </Link>
              <a 
                href="https://linkedin.com/company/terranova-ltd" 
                target="_blank"
                rel="noopener noreferrer"
                style={{zIndex: 1}}
                className="border-2 border-TerraNova-rust text-TerraNova-rust px-8 py-3 rounded-lg text-lg font-semibold hover:bg-TerraNova-rust hover:text-white transition duration-300 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.668v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                </svg>
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Follow Us Section */}
      <div className="bg-TerraNova-charcoal py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <span className="text-white font-medium">Follow Us:</span>
              <div className="flex items-center space-x-3">
                <a 
                  href="https://linkedin.com/company/terranova-ltd" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-TerraNova-cream hover:text-white transition duration-200"
                  aria-label="Follow us on LinkedIn"
                  style={{zIndex: 1}}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
